import type {
  FulfillmentMethod,
  TimeOfDay,
  TimeOfDayRange,
} from '@wix/ambassador-restaurants-v1-fulfillment-method/types';
import { DayOfWeek } from '@wix/ambassador-restaurants-v1-fulfillment-method/types';
import { DateTime } from 'luxon';

import type { Operation } from '../../../types/operation';

const DAY_OF_WEEK_TO_LUXON: Record<DayOfWeek, number> = {
  [DayOfWeek.MON]: 1,
  [DayOfWeek.TUE]: 2,
  [DayOfWeek.WED]: 3,
  [DayOfWeek.THU]: 4,
  [DayOfWeek.FRI]: 5,
  [DayOfWeek.SAT]: 6,
  [DayOfWeek.SUN]: 7,
};

const setTimeOfDay = (referenceDate: DateTime, time: TimeOfDay): DateTime =>
  referenceDate.set({
    hour: time.hours ?? 0,
    minute: time.minutes ?? 0,
    second: 0,
    millisecond: 0,
  });

const isInTimeRange = (time: DateTime, range: TimeOfDayRange): boolean => {
  const startTime = range.startTime
    ? setTimeOfDay(time, range.startTime)
    : time;
  const endTime = range.endTime ? setTimeOfDay(time, range.endTime) : time;

  if (endTime < startTime) {
    // Overnight window (e.g. 22:00–02:00): wraps past midnight, so the
    // candidate time is in-range when it sits after start OR before end.
    return time >= startTime || time <= endTime;
  }

  return time >= startTime && time <= endTime;
};

export const isFulfillmentAvailable = (
  fulfillment: FulfillmentMethod,
  operation: Operation,
): boolean => {
  const timeZone = fulfillment.availability?.timeZone ?? undefined;
  const availableTimes = fulfillment.availability?.availableTimes;

  if (!availableTimes?.length) {
    return false;
  }

  const asapPrepMinutes =
    operation.asapOptions?.maxInMinutes ??
    operation.asapOptions?.rangeInMinutes?.min ??
    0;
  const deliveryPrepMinutes =
    fulfillment.deliveryOptions?.deliveryTimeInMinutes ?? 0;

  const earliestOrderTime = DateTime.local({ zone: timeZone }).plus({
    minutes: asapPrepMinutes + deliveryPrepMinutes,
  });

  return availableTimes.some((availableTime) => {
    const dayNumber = availableTime.dayOfWeek
      ? DAY_OF_WEEK_TO_LUXON[availableTime.dayOfWeek]
      : undefined;

    if (dayNumber !== earliestOrderTime.weekday) {
      return false;
    }

    return !!availableTime.timeRanges?.some((range) =>
      isInTimeRange(earliestOrderTime, range),
    );
  });
};
